import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      setSubscribing(true);
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Subscribed successfully!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-leather-900 text-cream-100">
      {/* Newsletter Section */}
      <div className="px-6 md:px-12 lg:px-24 py-16 border-b border-leather-800">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="font-heading text-2xl md:text-3xl mb-4">Join Our Community</h3>
          <p className="text-cream-100/70 mb-8">
            Subscribe for exclusive offers, early access to new collections, and styling tips.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-transparent border-b border-cream-100/30 focus:border-gold-500 px-0 py-3 text-cream-100 placeholder:text-cream-100/50 focus:outline-none"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              disabled={subscribing}
              className="px-8 py-3 bg-gold-500 text-leather-900 text-sm tracking-widest uppercase hover:bg-gold-600 transition-colors disabled:opacity-50"
              data-testid="newsletter-submit"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </div>

      {/* Main Footer */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <h4 className="logo-text text-xl mb-6">BELT & TINGZ</h4>
            <p className="text-cream-100/70 text-sm leading-relaxed">
              Premium leather goods crafted with passion and precision. Every piece tells a story of artisanal excellence.
            </p>
          </div>

          {/* Shop */}
          <div>
            <h5 className="font-accent text-sm tracking-widest uppercase mb-6">Shop</h5>
            <ul className="space-y-3 text-cream-100/70">
              <li><Link to="/shop/belts" className="hover:text-gold-500">Belts</Link></li>
              <li><Link to="/shop/wallets" className="hover:text-gold-500">Wallets</Link></li>
              <li><Link to="/shop/dog_collars" className="hover:text-gold-500">Dog Collars</Link></li>
              <li><Link to="/shop/cologne" className="hover:text-gold-500">Cologne</Link></li>
            </ul>
          </div>

          {/* Help */}
          <div>
            <h5 className="font-accent text-sm tracking-widest uppercase mb-6">Help</h5>
            <ul className="space-y-3 text-cream-100/70">
              <li><Link to="/faq" className="hover:text-gold-500">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-gold-500">Contact Us</Link></li>
              <li><Link to="/faq#shipping" className="hover:text-gold-500">Shipping & Returns</Link></li>
              <li><Link to="/faq#care" className="hover:text-gold-500">Leather Care</Link></li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h5 className="font-accent text-sm tracking-widest uppercase mb-6">Connect</h5>
            <div className="flex gap-4 mb-6">
              <a href="#" className="text-cream-100/70 hover:text-gold-500" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-cream-100/70 hover:text-gold-500" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-cream-100/70 hover:text-gold-500" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
            <p className="text-cream-100/70 text-sm">
              Mon - Fri: 9am - 6pm EST<br />
              shop@leatherbylindsay.com
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-t border-leather-800">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-cream-100/50 text-sm">
          <p>&copy; {new Date().getFullYear()} Belt & Tingz. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gold-500">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold-500">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
